import { createContext, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ApplePayPaymentResponse } from "./types";
import { IBreachDetails } from "../../../../models/breach-details.interface";

type ApplePayContextType = {
  handleApplePayClick: (
    amount: number,
    breachDetails: IBreachDetails,
    email: string,
    setError: (error: string) => void
  ) => () => void;
  isApplePayAvailable: boolean;
};

export const ApplePayContext = createContext<ApplePayContextType>({
  handleApplePayClick: () => () => {},
  isApplePayAvailable: false,
});

export const ApplePayProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const history = useHistory();
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);

  const handleApplePayClick =
    (
      amount: number,
      breachDetails: IBreachDetails,
      email: string,
      setError: (error: string) => void
    ) =>
    () => {
      if (!ApplePaySession) {
        return;
      }

      const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
        countryCode: process.env.REACT_APP_APPLE_PAY_COUNTRY_CODE || "NZ",
        currencyCode: process.env.REACT_APP_APPLE_PAY_CURRENCY_CODE || "NZD",
        supportedNetworks:
          process.env.REACT_APP_APPLE_PAY_SUPPORTED_NETWORKS?.split(",") || [
            "visa",
            "masterCard",
            "amex",
          ],
        merchantCapabilities:
          (process.env.REACT_APP_APPLE_PAY_MERCHANT_CAPABILITIES?.split(
            ","
          ) as ApplePayJS.ApplePayMerchantCapability[]) || ["supports3DS"],
        total: {
          label:
            process.env.REACT_APP_APPLE_PAY_MERCHANT_NAME || "Central Park",
          amount: amount.toString(),
        },
      };

      const session = new ApplePaySession(3, paymentRequest);

      session.onvalidatemerchant = async (event) => {
        try {
          const response = await fetch(
            (process.env.REACT_APP_API_URL || "") +
              "/apple-pay/validate-merchant",
            {
              method: "POST",
              body: JSON.stringify({ validationUrl: event.validationURL }),
              headers: { "Content-Type": "application/json" },
            }
          );
          const responseData = await response.json();
          if (responseData.error) {
            session.abort();
            setError(
              "There was an error processing your payment. Please try again."
            );
            return;
          }
          session.completeMerchantValidation(responseData.merchantSession);
        } catch (error) {
          session.abort();
          setError(
            "There was an error processing your payment. Please try again."
          );
        }
      };

      session.onpaymentauthorized = async (event) => {
        console.log("payment authorized");
        const breachInformation = {
          amount: amount,
          ticketNbr: breachDetails.ticketNbr,
          vrm: breachDetails.vrm,
          siteName: breachDetails.location,
        };
        try {
          const response = await fetch(
            (process.env.REACT_APP_API_URL || "") +
              "/apple-pay/process-payment",
            {
              method: "POST",
              body: JSON.stringify({
                token: event.payment.token,
                breachInformation,
                email,
              }),
              headers: { "Content-Type": "application/json" },
            }
          );
          const data = (await response.json()) as ApplePayPaymentResponse;

          console.log("success", data);

          if (!data.error) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            history.push(
              `/confirmation/${breachInformation.ticketNbr}/${breachInformation.vrm}`
            );
          } else {
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            history.push(`/failed/?errorMessage=${data.message}`);
          }
        } catch (error) {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      };

      session.begin();
    };

  useEffect(() => {
    if (window.location.protocol !== "https:") {
      console.log("Apple Pay is not available");
      setIsApplePayAvailable(false);
      return;
    }
    const script = document.createElement("script");
    script.src =
      "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
    script.async = true;
    document.body.appendChild(script);
    setIsApplePayAvailable(true);
    return () => {
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <ApplePayContext.Provider
      value={{ handleApplePayClick, isApplePayAvailable }}
    >
      {children}
    </ApplePayContext.Provider>
  );
};

export const useApplePay = () => {
  return useContext(ApplePayContext);
};
